import { useTranslation } from "react-i18next";

import { useNavigate } from "react-router-dom";

import { useAtom } from "jotai";

import { atomAccountAddress } from "../atoms/atomsAccount";

import { deleteDatabase } from "../Utils/db";

import authService from "../services/authService";

import { Button } from "./ui/button";

interface SessionExpiredModalProps {
  open: boolean;
}

export function SessionExpiredModal({ open }: SessionExpiredModalProps) {
  const { t } = useTranslation("common");

  const navigate = useNavigate();

  const [, setAccount] = useAtom(atomAccountAddress);

  if (!open) return null;

  const handleLogin = async () => {
    // Ask backend to wipe eriol_session + eriol_csrf cookies. Best-effort —

    // if the session is already gone, this 401s but local state still gets

    // cleared below. (Browser path: this is the ONLY way to revoke the

    // HttpOnly cookie since JS can't touch it.)

    try {
      await authService.logout();
    } catch {
      // ignore
    }

    localStorage.removeItem("@Eriol/system/accessKey");

    localStorage.removeItem("@Eriol/system/userId");

    localStorage.removeItem("@Eriol/currentWorkspaceId");

    setAccount(null);

    window.dispatchEvent(new Event("auth-changed"));

    // Wipe IndexedDB on session expiry the same way voluntary logout does

    // (Profile.tsx). Otherwise PII (workspaces, tracks, integrations) from the

    // expired user persists for the next user on shared/kiosk devices.

    try {
      await deleteDatabase();
    } catch {
      // Best-effort — never block the redirect on cleanup.
    }

    try {
      // El preload expone `logout()` (→ ipcRenderer.invoke("auth-logout")).

      // No existe `electronAPI.invoke`, por eso la llamada anterior era un

      // no-op silencioso que nunca revocaba la sesión nativa.

      await window.electronAPI?.logout?.();
    } catch {
      // Best-effort
    }

    navigate("/", { replace: true });
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="w-full max-w-sm mx-4 rounded-xl border border-white/[0.08] bg-card p-6 shadow-2xl animate-[fadeIn_0.2s_ease-out]">
        <div className="flex flex-col items-center gap-4 text-center">
          {/* Lock icon */}
          <div className="size-14 rounded-2xl bg-destructive/10 border border-destructive/20 flex items-center justify-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="text-destructive"
            >
              <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
          </div>

          <div className="space-y-1.5">
            <h2 className="text-lg font-semibold text-foreground">
              {t("sessionExpired.title")}
            </h2>
            <p className="text-sm text-muted-foreground">
              {t("sessionExpired.message")}
            </p>
          </div>

          <Button onClick={handleLogin} className="w-full mt-2">
            {t("sessionExpired.action")}
          </Button>
        </div>
      </div>
    </div>
  );
}
